import { ArrowLeft, ListChecks, Share2 } from 'lucide-react';
import { useState } from 'react';

import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BulkInviteForm, RegistrationLinkForm } from '@/components/events/EventInviteForms';
import { useAuth } from '@/contexts/AuthContext';
import { useResidentProperty } from '@/hooks/useResidentProperty';

// RES-W-04 — Hosting a get-together.
// Two ways to invite a group: (1) share a public signup link so guests fill in
// their own details (registration link), (2) add everyone yourself (bulk).
//
// Instead of opening cold on two jargon tabs, we lead with the human question —
// "do you have everyone's number, or not?" — and present two plain-language
// choice cards. The resident picks one, fills the matching form, and can switch
// back at any time. This keeps the page approachable for non-technical users.
//
// The forms themselves live in `client/src/components/events/EventInviteForms.tsx`
// so they're shared with `/enterprise/events` and `/employee/events` — the
// server-side endpoints accept any role at or above `resident` in the
// hierarchy, so all three surfaces use the same posting code.

type Method = 'link' | 'list';

interface MethodOption {
  id: Method;
  icon: typeof Share2;
  title: string;
  description: string;
  recommended: boolean;
}

const METHODS: readonly MethodOption[] = [
  {
    id: 'link',
    icon: Share2,
    title: 'Let guests sign themselves in',
    description:
      'Share one link on WhatsApp. Each person fills in their own details and gets their own ' +
      "gate code. Best when you don't have everyone's number.",
    recommended: true,
  },
  {
    id: 'list',
    icon: ListChecks,
    title: "I'll add everyone myself",
    description:
      'Already have names and phone numbers? Add them here and we text each person their own ' +
      'gate code. Best for a short, known guest list.',
    recommended: false,
  },
];

function MethodCard({ option, onSelect }: { option: MethodOption; onSelect: (m: Method) => void }) {
  const Icon = option.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(option.id)}
      className="group flex w-full items-start gap-3 rounded-lg border border-[var(--bg-surface-raised)] p-4 text-left transition-colors hover:bg-[var(--bg-surface-raised)]"
    >
      <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-[var(--bg-surface-raised)] group-hover:bg-[var(--bg-surface)]">
        <Icon className="size-[18px] text-[var(--text-secondary)]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-[var(--text-primary)]">{option.title}</p>
          {option.recommended && (
            <span className="rounded-full border border-[var(--text-accent)] px-2 py-0.5 text-[10px] font-medium text-[var(--text-accent)]">
              Easiest
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-[var(--text-tertiary)]">{option.description}</p>
      </div>
    </button>
  );
}

export function EventsPage() {
  const { isLoading: isAuthLoading } = useAuth();
  const property = useResidentProperty();
  const [method, setMethod] = useState<Method | null>(null);

  if (isAuthLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <h1 className="text-2xl">Plan a get-together</h1>
        <p className="text-sm text-muted-foreground">
          We couldn&apos;t determine your property. Please open this page from your resident home.
        </p>
      </div>
    );
  }

  const active = method ? METHODS.find((m) => m.id === method) : null;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl">Plan a get-together</h1>
        <p className="text-sm text-muted-foreground">
          Having people over? Pick how you&apos;d like to invite them.
        </p>
      </div>

      {!active && (
        <Card>
          <CardContent className="space-y-3 pt-5">
            {METHODS.map((option) => (
              <MethodCard key={option.id} option={option} onSelect={setMethod} />
            ))}
          </CardContent>
        </Card>
      )}

      {active && (
        <Card>
          <CardContent className="space-y-4 pt-5">
            <div className="flex items-center justify-between gap-3 border-b border-[var(--bg-surface-raised)] pb-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[var(--text-primary)]">{active.title}</p>
                <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">{active.description}</p>
              </div>
              <button
                type="button"
                onClick={() => setMethod(null)}
                className="flex shrink-0 items-center gap-1 text-xs font-medium text-[var(--text-accent)] hover:underline"
              >
                <ArrowLeft className="size-3.5" />
                Change method
              </button>
            </div>

            {active.id === 'link' ? (
              <RegistrationLinkForm propertyId={property.propertyId} />
            ) : (
              <BulkInviteForm propertyId={property.propertyId} />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
